import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Attachment } from 'graphql-common';
import {
  useEdmDocumentationBulkFavoriteMutation,
  useEdmDocumentationBulkUnfavoriteMutation,
} from '@lib/graphql-common';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import ActionsMenu from '@lib/components/ActionsMenu/ActionsMenu';
import {
  IconButtonSizes,
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import { NavItem, NavItemTypeEnum } from '@lib/components/Menu/Menu';
import Icons from '@lib/components/Icons/Icons';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import DocumentVersionNotes from '@lib/components/DocumentationPreview/DocumentVersionNotes';
import { hasTextEditorValue } from '@lib/components/TextEditor/utils';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { APP_URLS } from 'constants/urls';
import { useAiChatInsert } from './AiChatInsertContext';
import { DocReferenceDocumentation } from './hooks/useDocReference';
import DocChip from './DocChip';
import styles from './AiAssistant.module.scss';

type Props = {
  documentation: NonNullable<DocReferenceDocumentation>;
};

// Stable NavItem leftIcon renderers — kept at module scope so they aren't
// recreated each render (react/no-unstable-nested-components). The built-in
// Icons.* helpers are already module-level; these cover icons not in that set.
const renderBookmarkAddIcon = () => (
  <MaterialIcon icon="bookmark_add" symbolsOutlined />
);
const renderBookmarkRemoveIcon = () => (
  <MaterialIcon icon="bookmark_remove" symbolsOutlined />
);
const renderInsertIcon = () => <MaterialIcon icon="reply" symbolsOutlined />;

// Resolved inline `:documentation{id}` reference: a compact document chip with a
// trailing menu mirroring the block preview's actions (View document / Version
// notes / Details / favourite toggle / Insert into chat). Rendered only once the
// reference resolves — pending/unavailable states stay as the muted, menu-less
// chip in DocReference.
function InlineDocChip({ documentation }: Props) {
  const { t } = useTranslation();
  const insertIntoChat = useAiChatInsert();

  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);

  const { id, name } = documentation;
  const notes = documentation.currentVersion?.notes;
  const attachment = documentation.currentVersion?.attachmentAttached as
    Attachment | null | undefined;
  const isFavorited = documentation.favorited ?? false;
  const hasNotes = hasTextEditorValue(notes || undefined);

  const label = name || t('document');

  const [bulkFavorite, { loading: favoriteLoading }] =
    useEdmDocumentationBulkFavoriteMutation({
      onError: (err) => showGraphQLErrors(err, t),
    });
  const [bulkUnfavorite, { loading: unfavoriteLoading }] =
    useEdmDocumentationBulkUnfavoriteMutation({
      onError: (err) => showGraphQLErrors(err, t),
    });
  const isFavoriteLoading = favoriteLoading || unfavoriteLoading;

  const onFavoriteToggle = useCallback(() => {
    const mutation = isFavorited ? bulkUnfavorite : bulkFavorite;
    const successMessage = isFavorited
      ? t('remove-from-favorites-success')
      : t('add-to-favorites-success');
    mutation({
      variables: { ids: [id] },
      update: (cache) => {
        cache.modify({
          id: cache.identify(documentation),
          fields: { favorited: () => !isFavorited },
        });
      },
      onCompleted: () => toast({ content: successMessage }),
    });
  }, [bulkFavorite, bulkUnfavorite, documentation, id, isFavorited, t]);

  const onDetails = useCallback(() => {
    const to = APP_URLS.dashboard.documentation.view.getDynamicPath({
      pathParts: { id },
    });
    window.open(to, '_blank')?.focus();
  }, [id]);

  const onInsertIntoChat = useCallback(() => {
    insertIntoChat({ id, name: label });
  }, [id, label, insertIntoChat]);

  // ActionsMenu's Popover auto-closes when a menu item is clicked, so the
  // handlers stay plain (no explicit close needed).
  const actions: NavItem<never>[] = useMemo(
    () => [
      {
        id: 'view-document',
        title: t('view-document'),
        type: NavItemTypeEnum.Secondary,
        leftIcon: Icons.view,
        onClick: () => setIsViewerOpen(true),
      },
      {
        id: 'version-notes',
        title: t('version-notes'),
        type: NavItemTypeEnum.Secondary,
        leftIcon: Icons.article,
        disabled: !hasNotes,
        onClick: () => setIsNotesOpen(true),
      },
      {
        id: 'details',
        title: t('details'),
        type: NavItemTypeEnum.Secondary,
        leftIcon: Icons.description,
        onClick: onDetails,
      },
      {
        id: 'favorite',
        title: isFavorited ? t('remove-from-favorites') : t('add-to-favorites'),
        type: NavItemTypeEnum.Secondary,
        leftIcon: isFavorited
          ? renderBookmarkRemoveIcon
          : renderBookmarkAddIcon,
        onClick: onFavoriteToggle,
      },
      {
        id: 'insert-into-chat',
        title: t('insert-into-chat'),
        type: NavItemTypeEnum.Secondary,
        leftIcon: renderInsertIcon,
        onClick: onInsertIntoChat,
      },
    ],
    [hasNotes, isFavorited, onDetails, onFavoriteToggle, onInsertIntoChat, t],
  );

  return (
    <>
      <DocChip
        label={label}
        trailing={
          <ActionsMenu
            actions={actions}
            icon="more_vert"
            iconButtonSize={IconButtonSizes.Small}
            iconButtonType={IconButtonTypes.SecondaryFilled}
            iconButtonClassName={styles.docRefChipMenuBtn}
            iconButtonRounded
          />
        }
      />
      <FileViewerModal
        attachment={attachment}
        attachmentName={name || undefined}
        documentationVersionId={documentation.currentVersion?.id}
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        useDownloadTool={false}
        useFavoriteTool
        onFavoriteToggle={onFavoriteToggle}
        isFavorited={isFavorited}
        isFavoriteLoading={isFavoriteLoading}
      />
      {isNotesOpen && hasNotes && (
        <DocumentVersionNotes
          isOpen={isNotesOpen}
          notes={notes || ''}
          onClose={() => setIsNotesOpen(false)}
        />
      )}
    </>
  );
}

export default InlineDocChip;
